import { Controller } from "@hotwired/stimulus"
import { enter, leave } from "el-transition"
import { createDepotChart } from "../charts/depot/chart"
import { seriesLiqui, seriesSteuer } from "../charts/depot/series"

// Depot-spezifischer Chart-Controller.
// Ziel: Orchestrierung + Turbo/Targets, keine Highcharts-Konfiguration hier.
export default class extends Controller {
	static targets = ["data", "notification", "charticon", "chartbutton", "zoombutton", "chartsettings", "bruttowertToggle"]
	static values = {
		depotId: String,
		url: String,
		detailFrameId: String,
	}

	initialize() {
		this.chart = createDepotChart()
		this.chart.setZoomCallbacks({
			onZoomIn: () => this.showZoombutton(),
			onZoomReset: () => this.hideZoombutton(),
		})
		this.chart.setChartSettingsCallbacks({
			onLegendToggle: (seriesId, willBeVisible) => {
				this.chart.toggleHiddenSeriesLocal(seriesId, willBeVisible)
				this.persistLegendVisibility(seriesId, willBeVisible)
			},
		})
	}

	connect() {
		this.berechnungStatusListenerRegistrieren()
		this.chart.data.id = this.depotIdValue
		this.chart.data.detailUrl = this.urlValue
		this.chart.data.detailFrameId = this.detailFrameIdValue

		// Initiale Daten aus dem DOM übernehmen (wenn vorhanden)
		if (this.hasDataTarget) this.setChartData()
		this.applyBruttowertToggleFromData()
		this.chart.init()
		this.syncBerechnungLoading()
	}

	disconnect() {
		this.berechnungStatusListenerEntfernen()
	}

	dataTargetConnected() {
		// Bei Turbo-Replace der Chartdaten: Chart aktualisieren
		this.setChartData()
		this.applyBruttowertToggleFromData()
		this.chart.update()
		this.aktualisiereLoadingNachChartdaten()
	}

	notificationTargetConnected() {
		this.showBerechnungLoading()
		enter(this.notificationTarget)
	}

	changeChartSeries({ currentTarget, params }) {
		if (this.chartbuttonTargets.includes(currentTarget)) {
			this.chartbuttonTargets.forEach((e) => e.removeAttribute("data-ui"))
			currentTarget.dataset.ui = "aktiv"
		} else {
			this.charticonTargets.forEach((e) => e.classList.toggle("hidden"))
		}

		switch (params.series) {
			case "steuer":
				this.chart.updateSeriesData(seriesSteuer(this.chart.data), "steuer")
				break
			case "liqui":
				this.chart.updateSeriesData(seriesLiqui(this.chart.data), "liqui")
				break
			default:
				console.error("Keine Series im Chart gefunden")
				this.charticonTargets.forEach((e) => e.classList.toggle("hidden"))
		}
	}

	// Wechsel Anzeige der Anlageklassen konsolidiert und differenziert
	changeBruttowertAnsicht({ currentTarget }) {
		if (!this.chart?.chart) return

		if (currentTarget.checked) {
			this.chart.data.status = "konsolidiert"
			this.chart.showAnlageklassenKonsolidiert()
		} else {
			this.chart.data.status = "differenziert"
			this.chart.showAnlageklassenDifferenziert()
		}
		this.persistBruttowertAnsicht(this.chart.data.status)
	}

	resetZoom() {
		if (!this.chart?.chart) return
		this.chart.data.anzeigeformat = "jahr"
		this.chart.chart.zoomOut()
	}

	// --- private

	setChartData() {
		if (!this.chart) this.chart = createDepotChart()
		const incoming = JSON.parse(this.dataTarget.dataset.chartdata)
		Object.assign(this.chart.data, incoming)
		this.chart.data.charteinstellungen ||= { hidden_series: [], bruttowert_ansicht: "konsolidiert" }
	}

	berechnungStatusListenerRegistrieren() {
		if (this.berechnungStatusListener) return

		this.berechnungStatusListener = (event) => this.berechnungStatusGeaendert(event)
		document.addEventListener("berechnung-status:geaendert", this.berechnungStatusListener)
	}

	berechnungStatusListenerEntfernen() {
		if (!this.berechnungStatusListener) return

		document.removeEventListener("berechnung-status:geaendert", this.berechnungStatusListener)
		this.berechnungStatusListener = null
	}

	berechnungStatusGeaendert(event) {
		if (!this.lokalerBerechnungStatus(event)) return

		if (event.detail.status === "laufend") {
			this.showBerechnungLoading()
		} else {
			this.hideBerechnungLoading()
		}
	}

	lokalerBerechnungStatus(event) {
		return event.target && typeof this.element.contains === "function" && this.element.contains(event.target)
	}

	syncBerechnungLoading() {
		if (this.berechnungLaeuft()) {
			this.showBerechnungLoading()
		} else {
			this.hideBerechnungLoading()
		}
	}

	aktualisiereLoadingNachChartdaten() {
		if (this.hasNotificationTarget) {
			this.chartdatenVerbunden = true
			this.hideNotification()
			return
		}

		if (this.chartdatenErstmalsVerbunden()) {
			this.syncBerechnungLoading()
			return
		}

		this.hideNotification()
	}

	chartdatenErstmalsVerbunden() {
		const erstmals = !this.chartdatenVerbunden
		this.chartdatenVerbunden = true
		return erstmals
	}

	berechnungLaeuft() {
		return (
			this.element.querySelector('[data-controller~="berechnung-status"][data-berechnung-status="laufend"]') !==
			null
		)
	}

	showBerechnungLoading() {
		if (this.chart?.chart) this.chart.showLoading()
	}

	hideBerechnungLoading() {
		if (this.chart?.chart) this.chart.hideLoading()
	}

	hideNotification() {
		if (!this.hasNotificationTarget) {
			this.hideBerechnungLoading()
			return
		}

		leave(this.notificationTarget).then(() => {
			this.hideBerechnungLoading()
			this.notificationTargets.forEach((element) => element.remove())
		})
	}

	hideZoombutton() {
		if (this.hasZoombuttonTarget) leave(this.zoombuttonTarget)
	}

	showZoombutton() {
		if (this.hasZoombuttonTarget) enter(this.zoombuttonTarget)
	}

	persistLegendVisibility(seriesId, _willBeVisible) {
		// Backend speichert bei dir per toggle! -> wir schicken nur die series_id.
		const token = document.querySelector('meta[name="csrf-token"]')?.content
		if (!token) return

		fetch(`/depots/${this.depotIdValue}/charteinstellungen`, {
			method: "PATCH",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				"X-CSRF-Token": token,
			},
			body: JSON.stringify({ series_id: seriesId }),
			credentials: "same-origin",
		}).catch((e) => console.error("Chart-Einstellungen konnten nicht gespeichert werden:", e))
	}

	applyBruttowertToggleFromData() {
		if (!this.hasBruttowertToggleTarget) return
		this.bruttowertToggleTarget.checked = this.chart?.data?.status === "konsolidiert"
	}

	persistBruttowertAnsicht(status) {
		const token = document.querySelector('meta[name="csrf-token"]')?.content
		if (!token) return

		fetch(`/depots/${this.depotIdValue}/charteinstellungen`, {
			method: "PATCH",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				"X-CSRF-Token": token,
			},
			body: JSON.stringify({ bruttowert_ansicht: status }),
			credentials: "same-origin",
		}).catch((e) => console.error("Chart-Einstellungen (Bruttowert-Ansicht) konnten nicht gespeichert werden:", e))
	}
}
